<template>
  <v-dialog v-model="dialog">
    <v-card>
      <v-card-title class="redColor d-flex text-white">
        <span class="headline">
          {{ $t("vehicle_entry.inventory_list.label") }}
        </span>
        <v-spacer />
        <v-btn variant="outlined" icon size="small"  color="white" @click="close">
          <v-icon> mdi-close </v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <!---------------------------------------CAR DETAILS--------------------------------------->
        <v-row class="mt-4 mb-6">
          <!--BRAND-->
          <v-col>
            <v-row class="d-flex flex-column">
              <span class="caption">{{
                $t("parts.advanced_search.brand")
              }}</span>
              <span v-if="car.brand" class="font-weight-bold">
                {{ car.brand.title }}
              </span>
            </v-row>
          </v-col>
          <!--MODEL-->
          <v-col>
            <v-row class="d-flex flex-column">
              <span class="caption">{{
                $t("parts.advanced_search.model")
              }}</span>
              <span v-if="car.model" class="font-weight-bold">
                {{ car.model.description }}
              </span>
            </v-row>
          </v-col>
          <!--VERSION-->
          <v-col>
            <v-row class="d-flex flex-column">
              <span class="caption">{{
                $t("parts.advanced_search.version")
              }}</span>
              <span v-if="car.version" class="font-weight-bold">
                {{ car.version.description }}
              </span>
            </v-row>
          </v-col>
          <!--ENGINE-->
          <v-col>
            <v-row class="d-flex flex-column">
              <span class="caption">{{
                $t("parts.advanced_search.engine")
              }}</span>
              <span v-if="car.version" class="font-weight-bold">
                {{ car.version.engineCode }}
              </span>
            </v-row>
          </v-col>
        </v-row>
        <!---------------------------------------PARTS LIST---------------------------------------->
        <template #progress>
          <v-progress-linear v-if="loading" color="red" indeterminate />
        </template>

        <v-list>
          <v-list-group
            v-for="(item, index) in parts"
            :key="item._id || index"
            v-model="activeStates[item._id]"
            :class="index % 2 === 0 ? 'bg-grey-lighten-4' : ''"
          >
            <template #activator="{ props }">
              <v-list-item v-bind="props">
                <v-list-item-title>
                  {{ item.category.it }}
                </v-list-item-title>
              </v-list-item>
            </template>

            <v-data-table
              :headers="headers"
              :items="item.items"
              :loading="loading"
              hide-default-footer
            >
              <!-- Vuetify 3 uses #loading instead of #progress -->
              <template #loading>
                <v-progress-linear color="red" indeterminate />
              </template>

              <template #body="{ items }">
                <tr
                  v-for="(i, ind) in items"
                  :key="ind"
                  :class="[
                    quantity[i.data.description.descriptionCode.toString()] <
                    i.count
                      ? 'bg-green'
                      : quantity[
                            i.data.description.descriptionCode.toString()
                          ] === i.count
                        ? 'bg-yellow'
                        : '',
                  ]"
                >
                  <!-- DESCRIPTION -->
                  <td>
                    <div class="text-xs font-weight-bold">
                      {{
                        i.data.description.descriptionId?.title?.[
                          $store.getters.language
                        ] || ""
                      }}
                    </div>
                    <div v-if="i.data.description.positionCode" class="text-xs">
                      {{ getPosition(i.data.description.positionCode) }}
                    </div>
                  </td>

                  <!-- QUANTITY -->
                  <td>{{ i.count }}</td>

                  <!-- ACTIONS -->
                  <td>
                    <v-btn
                      icon
                      variant="text"
                      @click="goToInventory(i.data.description.descriptionCode)"
                    >
                      <v-icon>mdi-open-in-new</v-icon>
                    </v-btn>
                  </td>

                  <!-- MINIMAL QUANTITY -->
                  <td>
                    <v-text-field
                      v-model="
                        quantity[i.data.description.descriptionCode.toString()]
                      "
                      type="number"
                      min="0"
                      density="compact"
                      hide-details
                      color="red-darken-2"
                      class="w-24"
                    />
                  </td>

                  <!-- NOTES -->
                  <td>
                    <v-text-field
                      v-model="
                        notes[i.data.description.descriptionCode.toString()]
                      "
                      density="compact"
                      hide-details
                      color="red-darken-2"
                    />
                  </td>

                  <!-- SELECT -->
                  <td>
                    <v-btn icon variant="text" @click="select(i)">
                      <v-icon
                        v-if="
                          selected[
                            i.data.description.descriptionCode.toString()
                          ]
                        "
                      >
                        mdi-checkbox-marked
                      </v-icon>
                      <v-icon v-else> mdi-checkbox-blank-outline </v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-list-group>
        </v-list>

        <!-- ACTIONS -->
        <v-row class="justify-center mt-2 px-2">
          <v-col cols="12" sm="auto" class="py-1">
            <v-btn
              color="primary"
              :loading="loadingSelect"
              class="vehicle-inventory-action-btn"
              @click="toggleSelectAll"
            >
              {{ $t("vehicle_entry.inventory_list.select_all") }}
              <v-icon end>
                {{
                  allSelected
                    ? "mdi-checkbox-marked"
                    : "mdi-checkbox-blank-outline"
                }}
              </v-icon>
            </v-btn>
          </v-col>
          <v-col cols="12" sm="auto" class="py-1">
            <v-btn
              color="primary"
              variant="flat"
              class="vehicle-inventory-action-btn"
              @click="exportToExcel"
            >
              {{ $t("vehicle_entry.inventory_list.export") }}
              <v-icon end>mdi-file-excel-outline</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import * as XLSX from "xlsx";
import getRequest from "../../api/getRequest";

export default {
  components: {},
  data: () => ({
    dialog: false,
    loading: false,
    loadingSelect: false,
    headers: [
      { title: "Description", sortable: false, key: "" },
      { title: "Actual quantity", sortable: false, key: "" },
      { title: "Actions", sortable: false, key: "" },
      { title: "Minimal quantity", sortable: false, key: "" },
      { title: "Notes", sortable: false, key: "" },
      { title: "Select", sortable: false, key: "" },
    ],
    pagination: {
      rowsPerPage: 5,
      page: 1,
    },
    car: { brand: {}, model: {}, version: {} },
    parts: [],
    total: 0,
    quantity: {},
    selected: {},
    notes: {},
    activeStates: {},
  }),
  computed: {
    allSelected() {
      const codes = this.parts.flatMap((part) =>
        part.items.map((item) =>
          item.data.description.descriptionCode.toString(),
        ),
      );
      return codes.length > 0 && codes.every((code) => this.selected[code]);
    },
  },
  watch: {
    pagination: {
      handler() {
        this.getParts();
      },
      deep: true,
    },
    dialog(value) {
      if (!value) this.close();
    },
  },
  methods: {
    setHeaders() {
      this.headers[0].title = this.$t(
        "vehicle_entry.inventory_list.description",
      );
      this.headers[1].title = this.$t(
        "vehicle_entry.inventory_list.actual_quantity",
      );
      this.headers[2].title = this.$t("vehicle_entry.inventory_list.actions");
      this.headers[3].title = this.$t(
        "vehicle_entry.inventory_list.minimal_quantity",
      );
      this.headers[4].title = this.$t("vehicle_entry.inventory_list.notes");
      this.headers[5].title = this.$t("vehicle_entry.inventory_list.select");
    },
    getParts() {
      this.loading = true;
      getRequest(
        "/part/vehicle-list-v2",
        `institution_id=${this.$store.getters.institutionId}&lang=${
          this.$store.getters.language
        }&brand=${this.car.brand.brandCode}&model=${
          this.car.model.modelCode
        }&version=${this.car.version.versionCode}&page=${
          this.pagination.page
        }&limit=${this.pagination.rowsPerPage}`,
      )
        .then((response) => {
          this.parts = response.parts;
          this.total = response.count;
          this.parts.forEach((item) => {
            this.activeStates[item._id] = true;
          });
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    open(car) {
      this.dialog = true;
      this.car = car;
      this.setHeaders();
      this.getParts();
    },
    close() {
      this.dialog = false;
      this.parts = [];
      this.total = 0;
      this.quantity = {};
      this.selected = {};
      this.notes = {};
      this.$emit("close");
    },
    select(item) {
      if (!this.selected[item.data.description.descriptionCode.toString()]) {
        this.selected[item.data.description.descriptionCode.toString()] = item;
      } else {
        delete this.selected[item.data.description.descriptionCode.toString()];
      }
      // Replate the object with its copy to invoke selection changes.
      this.parts = JSON.parse(JSON.stringify(this.parts));
    },
    selectAll() {
      this.loadingSelect = true;
      const selected = {};
      this.parts.forEach((part) => {
        part.items.forEach((item) => {
          selected[item.data.description.descriptionCode.toString()] = item;
        });
      });
      this.selected = selected;
      this.loadingSelect = false;
    },
    toggleSelectAll() {
      if (this.allSelected) this.selected = {};
      else this.selectAll();
    },
    async exportToExcel() {
      if (!Object.keys(this.selected).length) this.selectAll();
      const data = await this.exportData();

      // Create a worksheet
      const ws = XLSX.utils.json_to_sheet(data);

      // Create a workbook and append the worksheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "vehicle_inventory");

      // Trigger download
      XLSX.writeFile(wb, "vehicle_inventory.xlsx");
    },
    exportData() {
      const t = (k) => this.$t(`vehicle_entry.inventory_list.${k}`);
      const vehicle = [
        this.car.brand?.title,
        this.car.model?.description,
        this.car.version?.description,
      ]
        .filter(Boolean)
        .join(" ");
      return Object.keys(this.selected).map((key) => {
        const { description } = this.selected[key].data;
        const title =
          description.descriptionId?.title?.[this.$store.getters.language] ||
          "";
        const position = description.positionCode
          ? this.getPosition(description.positionCode)
          : "";
        return {
          [t("description")]: `${title} ${position}`.trim(),
          [t("vehicle")]: vehicle,
          [t("actual_quantity")]: this.selected[key].count,
          [t("minimal_quantity")]: this.quantity[key] ?? "",
          [t("notes")]: this.notes[key] ?? "",
        };
      });
    },
    goToInventory(code) {
      const { href } = this.$router.resolve({
        path: "/inventory",
        query: {
          titleCode: code,
          brand: this.car.brand.brandCode,
          model: this.car.model.modelCode,
          version: this.car.version.versionCode,
        },
      });
      window.open(href, "_blank", "noopener");
    },
    getPosition(code) {
      switch (code) {
        case "S001":
          return this.$t("left");
        case "S002":
          return this.$t("right");
        default:
          return "";
      }
    },
  },
};
</script>

<style scoped>
.vehicle-inventory-action-btn {
  width: auto;
}

@media (max-width: 600px) {
  .vehicle-inventory-action-btn {
    width: 100%;
  }
}
</style>
